<template>
  <DiagnosisCard heading="簡易ライフプラン">
    <template #content>
      <section :class="$style.result">
        <p>定年時の金融資産（推定）：</p>
        <p :class="assetsClass">
          <span :class="$style.assets__number">{{
            lastRemainingAssetsStrings.assets
          }}</span
          >{{ lastRemainingAssetsStrings.scale }}
        </p>
      </section>

      <section :class="$style.chart">
        <SimpleLifePlanningRow>
          <template #age>
            <p :class="[$style.sectionTitle, $style.sectionTitle__age]">
              経過年数
            </p>
          </template>
          <template #balance>
            <p :class="[$style.sectionTitle, $style.sectionTitle__balance]">
              年間貯金額
            </p>
          </template>
          <template #assets>
            <p :class="[$style.sectionTitle, $style.sectionTitle__asset]">
              金融資産
            </p>
          </template>
        </SimpleLifePlanningRow>

        <li v-for="(row, index) in rowsToShow" :key="index" :class="$style.row">
          <p v-if="index !== 0" :class="[$style.dot, $style.row__dot]">︙</p>
          <SimpleLifePlanningRowWithProps
            :row="row"
            :current-age="currentAge"
          />

          <!-- 教育費が増加したときに出すもの -->
          <DolphinChip
            v-if="shouldShowEducationCostPill(index)"
            :class="[$style.pill, $style['pill--educationFee']]"
          >
            <AlertTriangle :class="$style.pill__icon" />
            <p :class="$style.pill__text">教育費が増加！</p>
          </DolphinChip>

          <!-- 資産が赤字になったときに出すもの -->
          <DolphinChip
            v-if="shouldShowDeficitAssetPill(row)"
            :class="[$style.pill, $style['pill--asset']]"
          >
            <AlertTriangle :class="$style.pill__icon" />
            <p :class="$style.pill__text">貯蓄がマイナス（借金が発生）！</p>
          </DolphinChip>
        </li>
      </section>
    </template>
  </DiagnosisCard>
</template>

<script setup lang="ts">
import { computed, useCssModule } from 'vue'
import AlertTriangle from '@/assets/images/icons/fill/alert-triangle.svg?component'
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import DiagnosisCard from '@/components/molecules/DiagnosisCard.vue'
import SimpleLifePlanningRow from './SimpleLifePlanningRow.vue'
import SimpleLifePlanningRowWithProps from './SimpleLifePlanningRowWithProps.vue'
import { useFetchSimpleLifePlanningQuery } from './SimpleLifePlanning.generated'
import { EducationEventType, UserEventType } from '@/types/schema.generated'

const props = defineProps<{
  diagnosisUuid: string
}>()

type RowType = {
  deltaYear: number
  remainingAssets: number
  monthlyBalance: number
  educationCost: number
  events: string[]
}

const { result } = useFetchSimpleLifePlanningQuery({
  uuid: props.diagnosisUuid,
})

const rows = computed(
  () =>
    result.value?.diagnosis?.diagnosisSummary?.simpleLifePlanning.rows.map(
      row => ({
        deltaYear: row.deltaYear,
        remainingAssets: row.remainingAssets,
        monthlyBalance: row.monthlyBalance,
        educationCost: row.educationCost,
        events: row.events.map(value => {
          if ('childLabel' in value) {
            return `${value.childLabel}${convertEventToString(value.educationEventType)}`
          }
          return convertEventToString(value.userEventType)
        }),
      })
    ) ?? []
)

const convertEventToString = (
  event: UserEventType | EducationEventType
): string => {
  switch (event) {
    case UserEventType.ThePresentTime:
      return '現在'
    case UserEventType.MandatoryRetirement:
      return '定年'
    case EducationEventType.EsAdmission:
      return '小学校入学'
    case EducationEventType.JhsAdmission:
      return '中学校入学'
    case EducationEventType.HsAdmission:
      return '高校入学'
    case EducationEventType.UnivAdmission:
      return '大学入学'
    case EducationEventType.UnivGraduation:
      return '大学卒業'
  }
}

const lastRemainingAssets = computed(() =>
  rows.value.length > 0 ? rows.value[rows.value.length - 1].remainingAssets : 0
)

const lastRemainingAssetsStrings = computed(() => {
  if (Math.abs(lastRemainingAssets.value) >= 100_000_000) {
    // 1億円以上の場合は小数点第一位まで表示
    return {
      assets: (lastRemainingAssets.value / 100_000_000).toLocaleString(
        undefined,
        {
          maximumFractionDigits: 1,
        }
      ),
      scale: '億円',
    }
  }

  if (Math.abs(lastRemainingAssets.value) >= 10_000) {
    return {
      assets: Math.floor(lastRemainingAssets.value / 10_000).toString(),
      scale: '万円',
    }
  }

  return {
    assets: lastRemainingAssets.value.toString(),
    scale: '円',
  }
})

const rowsToShow = computed(() =>
  rows.value.filter(row => row.events.length > 0)
)

const RETIREMENT_AGE = 65
const currentAge = computed(() =>
  rows.value.length > 0
    ? RETIREMENT_AGE - rows.value[rows.value.length - 1].deltaYear
    : 0
)

const shouldShowEducationCostPill = (index: number): boolean => {
  if (index === 0) {
    return false
  }

  const row = rowsToShow.value[index]
  const prevRow = rowsToShow.value[index - 1]
  return row.educationCost > prevRow.educationCost
}

const shouldShowDeficitAssetPill = (row: RowType): boolean =>
  row.remainingAssets < 0

const style = useCssModule()
const assetsClass = computed(() => [
  style.assets,
  lastRemainingAssets.value >= 0
    ? style['assets--positive']
    : style['assets--negative'],
])
</script>

<style lang="scss" module>
.result {
  display: flex;
  align-items: center;
  font-weight: bold;
}

.assets {
  font-size: 14px;
  color: $color-text-and-object-accent-red;

  &__number {
    font-size: 24px;
  }

  &--positive {
    color: $color-text-and-object-accent-primary;
  }

  &--negative {
    color: $color-text-and-object-accent-red;
  }
}

.sectionTitle {
  font-size: 12px;
  font-weight: bold;

  &__balance {
    text-align: center;
  }

  &__asset {
    text-align: center;
  }
}

.row {
  list-style: none;

  &__dot {
    text-align: center;
  }

  &:nth-child(n + 2) {
    margin-top: 8px;
  }
}

.dot {
  line-height: 1;
  color: $color-blue-700;
}

.pill {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 0;

  &__icon {
    height: 12px;
  }

  &__text {
    font-weight: bold;
    font-size: 10px;
  }

  &--educationFee {
    background-color: $color-yellow-400;
  }

  &--asset {
    background-color: $color-red-500;
  }

  &:nth-child(n + 2) {
    margin-top: 8px;
  }
}
</style>
